import React, { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Separator } from "@/components/ui/separator";
import { Link } from "react-router-dom";
import CustomCheckbox from "@/components/ui/checkbox_custom";
import countries from "world-countries";
import { supabase } from "../lib/supabaseClient";

import { parsePhoneNumberFromString } from "libphonenumber-js";
import { PhoneInput } from "@/components/ui/phone-input";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

function normalizeCheckboxValue(v) {
  if (typeof v === "boolean") return v;
  if (v?.target && typeof v.target.checked === "boolean") return v.target.checked;
  return false;
}

function Request() {
  // ---- Schema ----
  const formSchema = z.object({
    firstName: z.string().trim().min(1, "Please enter your first name"),
    lastName: z.string().trim().min(1, "Please enter your last name"),

    curiosity: z.string().min(8, "A short line about your curiosity is helpful"),
    intro: z.string().min(12, "A few more words about you, please"),

    email: z.string().trim().email("Invalid email address"),

    phone_e164: z
      .string()
      .min(1, "Please provide a phone number")
      .refine((v) => {
        const p = parsePhoneNumberFromString(v);
        return Boolean(p?.isValid());
      }, "Please enter a valid phone number"),

    country: z.string().nonempty("Please select your country"),

    consent: z.literal(true, {
      errorMap: () => ({
        message:
          "Please consent so we can review your request and (if approved) create your member account.",
      }),
    }),

    marketingOptIn: z.boolean().default(false),
  });

  const stepSchemas = [
    z.object({ firstName: formSchema.shape.firstName, lastName: formSchema.shape.lastName }),
    z.object({ curiosity: formSchema.shape.curiosity }),
    z.object({ intro: formSchema.shape.intro }),
    z.object({
      email: formSchema.shape.email,
      phone_e164: formSchema.shape.phone_e164,
      country: formSchema.shape.country,
      consent: formSchema.shape.consent,
      marketingOptIn: formSchema.shape.marketingOptIn,
    }),
  ];

  const validateStep = async (stepIndex) => {
    const fields = stepFields[stepIndex];
    const ok = await form.trigger(fields);
    return ok;
  };


  const onInvalid = (errors) => {
    console.log("INVALID SUBMIT", errors);
    setAttempted((p) => ({ ...p, 3: true }));
  };
  // ---- Form ----
  const form = useForm({
    resolver: zodResolver(formSchema),
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    defaultValues: {
      firstName: "",
      lastName: "",
      curiosity: "",
      intro: "",
      email: "",
      phone_e164: "",
      country: "",
      consent: false,
      marketingOptIn: false,
    },
  });

  // ---- Progressive Steps ----
  const [step, setStep] = useState(0);
  const totalSteps = 4;

  const [attempted, setAttempted] = useState({ 0: false, 1: false, 2: false, 3: false });

  const stepFields = [
    ["firstName", "lastName"],
    ["curiosity"],
    ["intro"],
    ["email", "phone_e164", "country", "consent", "marketingOptIn"],
  ];

  // ---- Submission state ----
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);

  // ---- GSAP animation ----
  const stepRef = useRef(null);
  useEffect(() => {
    if (!stepRef.current) return;
    const ctx = gsap.context(() => {
      gsap.fromTo(
        stepRef.current,
        { autoAlpha: 0, y: 10 },
        { autoAlpha: 1, y: 0, duration: 0.5, ease: "power2.out" }
      );
    }, stepRef);
    return () => ctx.revert();
  }, [step]);

  useEffect(() => {
    if (!attempted[step]) {
      form.clearErrors(stepFields[step]);
    }
  }, [step, attempted, form]);

  async function ensureAnonSession() {
    const { data } = await supabase.auth.getUser();
    if (data?.user) return data.user;

    const { data: signed, error } = await supabase.auth.signInAnonymously();
    if (error) throw error;
    return signed.user;
  }

  // ---- Handlers ----
  const handleNext = async () => {
    setAttempted((p) => ({ ...p, [step]: true }));
    const ok = await validateStep(step);
    if (!ok) return;
    if (step < totalSteps - 1) setStep((s) => s + 1);
  };


  const handleBack = () => {
    if (step <= 0) return;
    const prevStep = step - 1;
    if (!attempted[prevStep]) {
      form.clearErrors(stepFields[prevStep]);
    }
    setStep(prevStep);
  };

  const onKeyDownAdvance = async (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (step < totalSteps - 1) await handleNext();
    }
  };

  // Country options (keep same UI; store label or iso — your current UI stores label)
  const countryOptions = countries
    .map((country) => ({
      label: country.name.common,
      value: country.cca2,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const sendConfirmationEmail = async ({ email, firstName, my_message }) => {
    const response = await fetch("/api/requestAccessConfirmation", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email,
        first_name: firstName,
        my_message,
      }),
    });

    if (!response.ok) {
      const text = await response.text().catch(() => "");
      throw new Error(`Email API failed: ${response.status} ${text}`);
    }

    return response.json();
  };

  const upsertKlaviyoApplicant = async (payload) => {
    const r = await fetch("/api/requestAccessKlaviyo", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data?.error || "Klaviyo API failed");
    return data;
  };


  // ---- Submit to Supabase ----
  const onSubmit = async (data) => {
    console.log('Submitted Data:', data);
    setAttempted((p) => ({ ...p, [step]: true }));
    setSubmitError("");

    setIsSubmitting(true);
    try {
      // Parse phone into parts (same as Launch form)
      const parsed = parsePhoneNumberFromString(data.phone_e164);
      if (!parsed || !parsed.isValid()) throw new Error("Invalid phone");

      const phone_country_iso2 = parsed.country || null;
      const phone_calling_code = `+${parsed.countryCallingCode}`;
      const phone_national = parsed.nationalNumber;
      const phone_e164 = parsed.number;

      const myMessage = `
      Thank you for your interest in Palais d’Antoia.
      
      Your request has been received and will be reviewed with care. We look at each submission individually, taking time to understand the intention and curiosity behind it.
      
      If approved, you will receive an invitation granting access to
      our private gatherings and collection platform.
      `.trim();

      /*       const { error } = await supabase.from("request_access").insert([
              {
                first_name: data.firstName.trim(),
                last_name: data.lastName.trim(),
      
                curiosity: data.curiosity,
                intro: data.intro,
      
                email: data.email.trim().toLowerCase(),
      
                phone_country_iso2,
                phone_calling_code,
                phone_national,
                phone_e164,
      
                country: data.country, // (note: your Select currently stores label — keep consistent or switch to ISO2 if you prefer)
      
                privacy_consent: data.consent,
                marketing_opt_in: data.marketingOptIn,
      
                source: "request_access",
                status: "new",
              },
            ]);
      
            if (error) throw error; */


      // Derive residence_country_iso2 from your stored country label
      const selected = countryOptions.find((c) => c.label === data.country);
      const residence_country_iso2 = selected?.value || null;
      const country_label = selected?.label || data.country || null;

      // ensure auth identity (anon)

      const authUser = await ensureAnonSession();
      const emailNorm = data.email.trim().toLowerCase();

      // 1) Create or reuse the canonical public user by email
      const { data: publicUserId, error: rpcErr } = await supabase
        .rpc("get_or_create_public_user", {
          p_email: emailNorm,
          p_first_name: data.firstName.trim(),
          p_last_name: data.lastName.trim(),
          p_source: "request_access",
        });

      if (rpcErr) throw rpcErr;

      const { error } = await supabase
        .from("request_access")
        .insert([{
          public_user_id: publicUserId,   // ✅ canonical identity by email
          auth_user_id: authUser.id,      // ✅ session identity (for RLS / auditing)

          first_name: data.firstName.trim(),
          last_name: data.lastName.trim(),
          curiosity: data.curiosity,
          intro: data.intro,
          email: emailNorm,

          phone_country_iso2,
          phone_calling_code,
          phone_national,
          phone_e164,

          country: country_label,
          residence_country_iso2,

          privacy_consent: data.consent,
          marketing_opt_in: data.marketingOptIn,

          source: "request_access",
          status: "new",
        }]);

      if (error) {
        // if already exists, treat as success (or show “already registered”)
        if (error.code === "23505") {
          setIsSubmitted(true);
          setError("already requested");
          return;
        }
        throw error;
      }



      // Send confirmation email (non-blocking option below)
      try {
        await sendConfirmationEmail({
          email: data.email.trim().toLowerCase(),
          firstName: data.firstName.trim(),
          my_message: myMessage,
        });
      } catch (emailErr) {
        console.error("Confirmation email failed:", emailErr);
        // Choose behavior:
        // A) still show Thank you (recommended):
        // do nothing
        // B) block thank-you and show error instead:
        // throw new Error("Request submitted, but confirmation email failed.");
      }

      // helper to send to Klaviyo
      try {
        await upsertKlaviyoApplicant({
          email: data.email.trim().toLowerCase(),
          first_name: data.firstName.trim(),
          last_name: data.lastName.trim(),
          phone_e164,
          phone_country_iso2,
          residence_country_iso2,
          country_label: data.country,
          privacy_consent: data.consent,
          marketing_opt_in: data.marketingOptIn,
          source: "request_access",
          status: "new",

        });
      } catch (klaviyoErr) {
        console.error("Klaviyo sync failed:", klaviyoErr);
        // recommended: do not block user success
      }

      setIsSubmitted(true);
      form.reset();
      setStep(0);
      setAttempted({ 0: false, 1: false, 2: false, 3: false });
    } catch (err) {
      setSubmitError(err?.message || "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ---- UI ----
  if (isSubmitted) {
    return (
      <div className="text-center max-w-prose mx-auto my-24">
        <h1 className="font-sans text-black font-medium text-base mx-auto text-center my-4">
          Thank you
        </h1>
        <p className="text-3xl font-kalice">
          We received your request. We’ll review it and get back to you.
        </p>

        <div className="mt-10 flex justify-center">
          <Button
            className="rounded-none text-sm font-sans px-12"
            onClick={() => setIsSubmitted(false)}
          >
            Return →
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Header / Intro */}
      <div className="text-center max-w-prose mx-auto my-24">
        <h1 className="font-sans text-black font-medium text-base mx-auto text-center my-4">
          Become our newest guest
        </h1>
        <p className="text-3xl font-kalice px-4">
          Members of <span className="italic">Palais d&apos;Antoia</span> are invited
          to curated gatherings & receive access to our collection platform
        </p>
      </div>

      <Separator className="border border-white/20 my-24" />

      {/* Progressive Form */}
      <div className="max-w-xl mx-auto my-12 px-6">
        <h1 className="text-center text-3xl font-medium mb-6 font-kalice">
          Request access
        </h1>

        {/* Progress indicator */}
        <div className="flex items-center justify-center gap-2 mb-6">
          {Array.from({ length: totalSteps }).map((_, i) => (
            <span
              key={i}
              className={[
                "h-1 w-24 rounded-full transition-all",
                i <= step ? "bg-black" : "bg-neutral-300",
              ].join(" ")}
            />
          ))}
        </div>

        <div className="mb-48">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-6"
              aria-live="polite"
            >
              <div ref={stepRef} className="min-h-[0px] mt-24">
                {/* Step 0: first + last */}
                {step === 0 && (
                  <div className="space-y-6">
                    <p className="font-kalice text-3xl font-medium block mb-6 text-center">
                      What is your name?
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="firstName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="font-sans text-sm">First name</FormLabel>
                            <FormControl>
                              <Input
                                autoFocus
                                placeholder="First name"
                                className="text-black border-gray-700 border-2 bg-transparent"
                                {...field}
                                onKeyDown={onKeyDownAdvance}
                              />
                            </FormControl>
                            {attempted[step] && <FormMessage />}
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="lastName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="font-sans text-sm">Last name</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Last name"
                                className="text-black border-gray-700 border-2 bg-transparent"
                                {...field}
                                onKeyDown={onKeyDownAdvance}
                              />
                            </FormControl>
                            {attempted[step] && <FormMessage />}
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                )}

                {/* Step 1 */}
                {step === 1 && (
                  <FormField
                    control={form.control}
                    name="curiosity"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="font-kalice text-3xl font-medium block text-center mb-6">
                          What sparks your curiosity for Antoia?
                        </FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Type here.."
                            className="text-black text-center border-gray-700 border-2 bg-transparent resize-none h-28"
                            {...field}
                            onKeyDown={onKeyDownAdvance}
                          />
                        </FormControl>
                        {attempted[step] && <FormMessage />}
                      </FormItem>
                    )}
                  />
                )}

                {/* Step 2 */}
                {step === 2 && (
                  <FormField
                    control={form.control}
                    name="intro"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="font-kalice text-3xl font-medium block text-center mt-20 mb-6">
                          What makes you, you?
                        </FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Please introduce yourself.."
                            className="text-black text-center border-gray-700 border-2 bg-transparent resize-none h-32"
                            {...field}
                            onKeyDown={onKeyDownAdvance}
                          />
                        </FormControl>
                        {attempted[step] && <FormMessage />}
                      </FormItem>
                    )}
                  />
                )}

                {/* Step 3 */}
                {step === 3 && (
                  <div className="space-y-4">
                    <p className="font-kalice text-3xl font-medium block text-center mt-20 mb-6">
                      Contact details
                    </p>

                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="font-sans text-sm">Email</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="you@domain.com"
                              className="text-black text-center border-gray-700 border-2 bg-transparent"
                              {...field}
                              onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
                            />
                          </FormControl>
                          {attempted[step] && <FormMessage />}
                        </FormItem>
                      )}
                    />

                    {/* ✅ PhoneInput like Launch form */}
                    <FormField
                      control={form.control}
                      name="phone_e164"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="font-sans text-sm">Phone</FormLabel>
                          <FormControl>
                            <PhoneInput
                              defaultCountry="NL"
                              international
                              placeholder="Enter phone number"
                              value={field.value}
                              onChange={(v) => field.onChange(v ?? "")}
                            />
                          </FormControl>
                          {attempted[step] && <FormMessage />}
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="country"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="font-sans text-sm">Country</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger className="text-black border-2 border-black bg-transparent my-2">
                                <SelectValue placeholder="Country" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent className="bg-white">
                              {countryOptions.map((c) => (
                                <SelectItem key={c.value} value={c.label}>
                                  {c.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {attempted[step] && <FormMessage />}
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="consent"
                      render={({ field }) => (
                        <FormItem className="flex items-center space-x-4">
                          <FormControl>
                            <CustomCheckbox
                              checked={!!field.value}
                              onChange={(v) => field.onChange(normalizeCheckboxValue(v))}
                            />
                          </FormControl>
                          <div className="space-y-1 leading-tight">
                            <FormLabel className="font-sans text-sm items-center">
                              I consent to my personal data being processed to assess my
                              application and agree to the&nbsp;
                              <Link
                                to="/privacystatement"
                                target="_blank"
                                className="font-sans_bold text-black underline hover:text-black/80"
                              >
                                privacy statement
                              </Link>
                              .
                            </FormLabel>
                            {attempted[step] && <FormMessage />}
                          </div>
                        </FormItem>
                      )}
                    />

                    {/* ✅ Marketing opt-in */}
                    <FormField
                      control={form.control}
                      name="marketingOptIn"
                      render={({ field }) => (
                        <FormItem className="flex items-center space-x-4">
                          <FormControl>
                            <CustomCheckbox
                              checked={!!field.value}
                              onChange={(v) => field.onChange(normalizeCheckboxValue(v))}
                            />
                          </FormControl>
                          <div className="space-y-1 leading-tight">
                            <FormLabel className="font-sans text-sm items-center">
                              Send me occasional Antoia updates (you can unsubscribe anytime).
                            </FormLabel>
                            {attempted[step] && <FormMessage />}
                          </div>
                        </FormItem>
                      )}
                    />
                  </div>
                )}
              </div>

              {/* Error */}
              {submitError && (
                <p className="text-sm font-sans text-red-600 text-center">{submitError}</p>
              )}

              {/* Navigation */}
              <div className="flex justify-end p-2 space-x-2">
                <Button
                  type="button"
                  className="rounded-none text-sm font-sans bg-[#6d7770] w-[30%] disabled:bg-black/20"
                  onClick={handleBack}
                  disabled={step === 0 || isSubmitting}
                >
                  Back
                </Button>

                {step < totalSteps - 1 ? (
                  <Button
                    type="button"
                    className="group rounded-none text-sm font-sans w-[30%]"
                    onClick={handleNext}
                    disabled={isSubmitting}
                  >
                    Next{" "}
                    <span className="inline-block ml-2 transition-transform duration-300 group-hover:translate-x-3">
                      →
                    </span>
                  </Button>
                ) : (
                  <Button
                    type="button"
                    onClick={() => form.handleSubmit(onSubmit, onInvalid)()}
                    className="group rounded-none text-sm font-sans w-[30%]"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Submitting..." : "Submit "}
                    <span className="inline-block ml-2 transition-transform duration-300 group-hover:translate-x-3">
                      →
                    </span>
                  </Button>

                )}
              </div>
            </form>
          </Form>
        </div>
      </div>
    </>
  );
}

export default Request;

